/**
 * HYPSEYA — Monthly Integration Report
 * A GPT-4o generated mentor letter synthesising 30 days of inner work.
 * The most personal, high-value document the app produces.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Palette ──────────────────────────────────────────────────────────────────
const TEAL   = '#2D8B87';
const DARK   = '#1E6B68';
const AMBER  = '#B8906A';
const CREAM  = 'rgba(247,243,236,0.97)';
const PAPER  = 'rgba(239,231,221,0.80)';
const SHADOW = 'rgba(30,107,104,0.08)';

// ── Tiny helpers ─────────────────────────────────────────────────────────────
function Tag({ children, color = TEAL }) {
  return (
    <span style={{
      display: 'inline-block',
      background: `${color}18`,
      color,
      border: `1px solid ${color}30`,
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      padding: '3px 10px',
    }}>{children}</span>
  );
}

function StatPill({ label, value, unit = '', delta = null }) {
  return (
    <div style={{
      background: CREAM,
      border: `1px solid rgba(30,107,104,0.10)`,
      borderRadius: '14px',
      padding: '14px 18px',
      textAlign: 'center',
      minWidth: '100px',
      flex: '1 1 100px',
    }}>
      <div style={{ fontSize: '22px', fontWeight: 700, color: DARK, letterSpacing: '-0.5px' }}>
        {value != null ? value : '—'}{unit}
      </div>
      {delta != null && (
        <div style={{ fontSize: '11px', color: delta >= 0 ? TEAL : '#A33B2B', fontWeight: 600, marginBottom: '2px' }}>
          {delta >= 0 ? `+${delta}` : delta} pts
        </div>
      )}
      <div style={{ fontSize: '11px', color: '#8B9B97', fontWeight: 500, marginTop: '2px' }}>{label}</div>
    </div>
  );
}

function SectionCard({ tag, tagColor = TEAL, title, body, icon }) {
  return (
    <div style={{
      background: CREAM,
      border: `1px solid rgba(30,107,104,0.10)`,
      borderRadius: '20px',
      padding: '26px',
      marginBottom: '16px',
      boxShadow: `0 2px 16px ${SHADOW}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        {icon && <span style={{ fontSize: '18px' }}>{icon}</span>}
        <Tag color={tagColor}>{tag}</Tag>
      </div>
      {title && (
        <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 700, color: '#1A2E2C', letterSpacing: '-0.3px' }}>
          {title}
        </h3>
      )}
      <p style={{ margin: 0, fontSize: '15px', lineHeight: '1.7', color: '#2C4A47' }}>{body}</p>
    </div>
  );
}

// ── Coherence meter ───────────────────────────────────────────────────────────
function CoherenceMeter({ score }) {
  const label = score >= 70 ? 'Integrated' : score >= 40 ? 'Developing' : 'Fragmented';
  const color = score >= 70 ? TEAL : score >= 40 ? AMBER : '#A33B2B';
  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ fontSize: '12px', color: '#6B8B87', fontWeight: 600 }}>NARRATIVE COHERENCE</span>
        <span style={{ fontSize: '12px', color, fontWeight: 700 }}>{label} · {score}/100</span>
      </div>
      <div style={{ height: '5px', background: 'rgba(0,0,0,0.07)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${score}%`, background: color,
          borderRadius: '3px', transition: 'width 1.2s cubic-bezier(0.4,0,0.2,1)',
        }} />
      </div>
      <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#8B9B97', lineHeight: 1.5 }}>
        Measures how integrated, consistent, and self-aware your written reflections are across the month.
      </p>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ onGenerate, generating }) {
  return (
    <div style={{
      background: CREAM, border: `1px solid rgba(30,107,104,0.10)`,
      borderRadius: '24px', padding: '48px 32px', textAlign: 'center',
      boxShadow: `0 4px 24px ${SHADOW}`,
    }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📖</div>
      <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 700, color: '#1A2E2C' }}>
        Your first Integration Report
      </h2>
      <p style={{ margin: '0 0 28px', fontSize: '15px', color: '#5A7A76', lineHeight: 1.7, maxWidth: '380px', marginLeft: 'auto', marginRight: 'auto' }}>
        HYPSEYA has been silently observing your inner work. This report is a letter from a mentor who has seen everything — your sessions, your reflections, your patterns.
      </p>
      <button
        onClick={onGenerate}
        disabled={generating}
        style={{
          background: generating ? '#8BB8B5' : `linear-gradient(135deg, ${TEAL}, ${DARK})`,
          color: '#fff', border: 'none', borderRadius: '16px',
          padding: '14px 32px', fontSize: '15px', fontWeight: 600,
          cursor: generating ? 'not-allowed' : 'pointer',
          boxShadow: generating ? 'none' : `0 4px 16px rgba(45,139,135,0.30)`,
          transition: 'all 0.2s ease',
        }}
      >
        {generating ? 'Generating your report…' : 'Generate my Integration Report'}
      </button>
      <p style={{ margin: '16px 0 0', fontSize: '12px', color: '#8B9B97' }}>
        Powered by GPT-4o · Takes about 10 seconds
      </p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function IntegrationReport() {
  const navigate = useNavigate();
  const [report, setReport]       = useState(null);
  const [stats, setStats]         = useState(null);
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError]         = useState(null);
  const [tab, setTab]             = useState('current');   // 'current' | 'archive'

  const [userId] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').user_id; } catch { return null; }
  });

  useEffect(() => {
    if (userId) {
      fetchLatest();
      fetchHistory();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const fetchLatest = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/training/monthly-report/${userId}/latest`);
      if (res.data.exists) {
        setReport(res.data.report);
        setStats(res.data.stats);
      }
    } catch (e) {
      // no report yet, will show empty state
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/training/monthly-report/${userId}/history`);
      setHistory(res.data.reports || []);
    } catch (e) {}
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/training/monthly-report/${userId}/generate`);
      if (res.data.generated) {
        setReport(res.data.report);
        setStats(res.data.stats);
        fetchHistory();
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const page = {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #EEF5F4 0%, #F5EFE8 60%, #EBE4DA 100%)',
    padding: '0 0 80px',
    fontFamily: "'Inter', -apple-system, sans-serif",
  };

  const container = { maxWidth: '680px', margin: '0 auto', padding: '0 20px' };

  // ── Header ──────────────────────────────────────────────────────────────────
  const header = (
    <div style={{
      background: `linear-gradient(135deg, ${DARK} 0%, ${TEAL} 100%)`,
      padding: '56px 24px 40px',
      marginBottom: '32px',
    }}>
      <div style={container}>
        <button
          onClick={() => navigate('/growth')}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '10px',
            color: '#fff', padding: '8px 14px', fontSize: '13px', cursor: 'pointer',
            marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          ← Growth
        </button>
        <Tag color="#FFFFFF">Monthly Report</Tag>
        <h1 style={{ margin: '12px 0 6px', fontSize: '32px', fontWeight: 800,
          color: '#fff', letterSpacing: '-0.8px', lineHeight: 1.15 }}>
          Integration Report
        </h1>
        <p style={{ margin: 0, fontSize: '15px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
          A letter from HYPSEYA — synthesising your inner work this month.
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={page}>
        {header}
        <div style={{ ...container, textAlign: 'center', paddingTop: '60px' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>✦</div>
          <p style={{ color: '#6B8B87' }}>Loading your report…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      {header}
      <div style={container}>

        {/* Tab switcher (only if history exists) */}
        {history.length > 1 && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
            {['current', 'archive'].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{
                background: tab === t ? DARK : 'rgba(30,107,104,0.07)',
                color: tab === t ? '#fff' : DARK,
                border: 'none', borderRadius: '12px',
                padding: '8px 20px', fontSize: '13px', fontWeight: 600,
                cursor: 'pointer', textTransform: 'capitalize',
              }}>{t === 'current' ? 'This Month' : 'Archive'}</button>
            ))}
          </div>
        )}

        {/* ── ARCHIVE TAB ── */}
        {tab === 'archive' && (
          <div>
            <h2 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 700, color: '#1A2E2C' }}>
              Previous Reports
            </h2>
            {history.map((h, i) => (
              <div key={i} style={{
                background: CREAM, border: '1px solid rgba(30,107,104,0.10)',
                borderRadius: '16px', padding: '20px', marginBottom: '12px',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = `0 4px 20px ${SHADOW}`}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#1A2E2C', marginBottom: '4px' }}>
                      {h.month}
                    </div>
                    <p style={{ margin: '0', fontSize: '13px', color: '#5A7A76', lineHeight: 1.5, maxWidth: '400px' }}>
                      {h.report?.opening ? h.report.opening.slice(0, 120) + '…' : 'Report generated'}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '16px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: TEAL }}>
                      {h.stats?.current_eri?.toFixed(0) || '—'}
                    </div>
                    <div style={{ fontSize: '10px', color: '#8B9B97' }}>ERI</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── CURRENT REPORT TAB ── */}
        {tab === 'current' && (
          <>
            {!report ? (
              <EmptyState onGenerate={handleGenerate} generating={generating} />
            ) : (
              <>
                {/* Stats row */}
                {stats && (
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '24px' }}>
                    <StatPill label="Sessions" value={stats.total_sessions} unit="/30" />
                    <StatPill label="ERI Score" value={stats.current_eri?.toFixed(0)} delta={stats.eri_delta} />
                    <StatPill
                      label="Avg Shift"
                      value={stats.avg_delta != null ? (stats.avg_delta > 0 ? `+${stats.avg_delta}` : stats.avg_delta) : '—'}
                    />
                    <StatPill label="Coherence" value={stats.coherence_score} unit="/100" />
                  </div>
                )}

                {/* Narrative coherence meter */}
                {stats?.coherence_score != null && (
                  <div style={{
                    background: CREAM, border: `1px solid rgba(30,107,104,0.10)`,
                    borderRadius: '20px', padding: '22px', marginBottom: '16px',
                  }}>
                    <CoherenceMeter score={stats.coherence_score} />
                  </div>
                )}

                {/* Opening letter */}
                {report.opening && (
                  <div style={{
                    background: `linear-gradient(135deg, rgba(30,107,104,0.04) 0%, rgba(184,144,106,0.04) 100%)`,
                    border: `1px solid rgba(30,107,104,0.12)`,
                    borderLeft: `4px solid ${TEAL}`,
                    borderRadius: '20px',
                    padding: '28px',
                    marginBottom: '20px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                      <span style={{ fontSize: '16px' }}>✦</span>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: TEAL, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        {report.month || 'This Month'}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '16px', lineHeight: '1.8', color: '#1A2E2C', fontStyle: 'italic' }}>
                      {report.opening}
                    </p>
                  </div>
                )}

                {/* Patterns */}
                {Array.isArray(report.patterns) && report.patterns.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <h2 style={{ margin: '0 0 14px', fontSize: '14px', fontWeight: 700,
                      color: '#8B9B97', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      Patterns observed
                    </h2>
                    {report.patterns.map((p, i) => (
                      <SectionCard
                        key={i}
                        tag="Pattern"
                        icon={['🔁', '🌀', '⚡', '🌊'][i % 4]}
                        title={p.title}
                        body={p.body}
                      />
                    ))}
                  </div>
                )}

                {/* Shadow zone */}
                {report.shadow_zone && (
                  <SectionCard
                    tag="Shadow Zone"
                    tagColor={AMBER}
                    icon="🌑"
                    title={report.shadow_zone.title}
                    body={report.shadow_zone.body}
                  />
                )}

                {/* Breakthrough */}
                {report.breakthrough && (
                  <SectionCard
                    tag="Breakthrough"
                    tagColor="#5A8F5E"
                    icon="✦"
                    title={report.breakthrough.title}
                    body={report.breakthrough.body}
                  />
                )}

                {/* Intention */}
                {report.intention && (
                  <div style={{
                    background: `linear-gradient(135deg, ${DARK}, ${TEAL})`,
                    borderRadius: '20px', padding: '28px', marginBottom: '16px',
                    boxShadow: `0 4px 24px rgba(30,107,104,0.20)`,
                  }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.65)',
                      letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>
                      Intention for next month
                    </div>
                    <p style={{ margin: 0, fontSize: '17px', lineHeight: '1.7', color: '#fff', fontWeight: 500 }}>
                      {report.intention}
                    </p>
                  </div>
                )}

                {/* Closing */}
                {report.closing && (
                  <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
                    <p style={{ margin: 0, fontSize: '16px', color: '#4A7A76', fontStyle: 'italic', lineHeight: 1.7 }}>
                      "{report.closing}"
                    </p>
                    <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#A0B4B0', fontWeight: 600, letterSpacing: '0.06em' }}>
                      — HYPSEYA
                    </p>
                  </div>
                )}

                {/* Regenerate */}
                <div style={{ textAlign: 'center', paddingTop: '32px' }}>
                  <button
                    onClick={handleGenerate}
                    disabled={generating}
                    style={{
                      background: generating ? '#C0CFCD' : 'rgba(30,107,104,0.08)',
                      color: DARK, border: `1px solid rgba(30,107,104,0.20)`,
                      borderRadius: '14px', padding: '11px 24px',
                      fontSize: '13px', fontWeight: 600, cursor: generating ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {generating ? 'Regenerating…' : '↻ Regenerate report'}
                  </button>
                </div>
              </>
            )}

            {error && (
              <div style={{ background: 'rgba(163,59,43,0.08)', border: '1px solid rgba(163,59,43,0.20)',
                borderRadius: '12px', padding: '14px 18px', marginTop: '16px', color: '#A33B2B', fontSize: '14px' }}>
                {error}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
